import { Check, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import type { GetChangeRequestsResponse } from "@shared/types/payloads/GetChangeRequestsResponse";

interface RequestDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: GetChangeRequestsResponse | null;
  onAcceptChangeOrder: (request: GetChangeRequestsResponse) => Promise<void>;
}

export function RequestDetailDialog({
  open,
  onOpenChange,
  request,
  onAcceptChangeOrder,
}: RequestDetailDialogProps) {
  if (!request) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Change Request Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="uppercase text-xs">
              {request.type}
            </Badge>
            <Badge
              className={
                request.status === "pending"
                  ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                  : request.status === "approved"
                  ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                  : "bg-red-500/10 text-red-400 border-red-500/20"
              }
            >
              {request.status.charAt(0).toUpperCase() +
                request.status.slice(1)}
            </Badge>
          </div>

          <div>
            <Label className="text-muted-foreground text-xs uppercase tracking-wide">
              Requested Changes
            </Label>
            <ul className="mt-2 space-y-2">
              {request.changes.map((change, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm p-3 bg-white/[0.02] border border-white/5 rounded-lg"
                >
                  <FileText className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  {change}
                </li>
              ))}
            </ul>
          </div>

          {request.approvalReason && (
            <div>
              <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                Agency Response
              </Label>
              <div className="mt-2 p-3 bg-emerald-500/5 border border-emerald-500/10 rounded-lg">
                <p className="text-sm text-emerald-300">
                  {request.approvalReason}
                </p>
              </div>
            </div>
          )}

          {request.rejectionReason && (
            <div>
              <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                Rejection Reason
              </Label>
              <div className="mt-2 p-3 bg-red-500/5 border border-red-500/10 rounded-lg">
                <p className="text-sm text-red-300">
                  {request.rejectionReason}
                </p>
              </div>
            </div>
          )}

          {request.changeOrders.length > 0 && (
            <div className="space-y-4 pt-4 border-t border-white/5">
              <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                Change Order
              </Label>

              <div className="space-y-2">
                <p className="text-sm font-medium">New Deliverables:</p>
                {request.changeOrders[0].deliverables.map((d) => (
                  <div
                    key={d.id}
                    className="p-3 bg-white/[0.02] border border-white/5 rounded-lg"
                  >
                    <p className="font-medium text-sm">{d.title}</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      {d.description}
                    </p>
                    <p className="text-xs text-primary mt-2">
                      Due: {d.dueDate}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between p-4 bg-primary/5 border border-primary/10 rounded-xl">
                <span className="text-sm font-medium">Additional Cost:</span>
                <span className="text-lg font-bold text-primary">
                  {request.changeOrders[0].paymentTerms.currency}{" "}
                  {request.changeOrders[0].paymentTerms.totalAmount.toLocaleString()}
                </span>
              </div>

              {request.changeOrders[0].status !== "approved" && (
                <Button
                  className="w-full"
                  onClick={() => onAcceptChangeOrder(request)}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Accept & Sign Amendment
                </Button>
              )}

              {request.changeOrders[0].status === "approved" && (
                <div className="flex items-center gap-2 p-3 bg-emerald-500/5 border border-emerald-500/10 rounded-lg">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-emerald-300">
                    Change order accepted and signed
                  </span>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground pt-2 border-t border-white/5">
            Submitted:{" "}
            {new Date(request.createdAt).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

